import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * What a newly-mapped person starts with (Task 5, admin half).
 *
 * THIS IS A TEMPLATE, AND THE SCREEN HAS TO SAY SO.
 * Saving here changes what the NEXT person inherits when they are mapped. It
 * does not reach into anybody's existing settings, and the copy below states
 * that in as many words rather than leaving an admin to find out by surprising
 * a colleague. The epic gives every person an opt-out; a default that rewrote
 * current rows would quietly undo one, so the server does not do it and this
 * screen does not imply that it might.
 *
 * It lives on the admin-only Integrations page, beside the workspace
 * connection and the message preview. A person's OWN settings are somewhere
 * else entirely — Settings → Notifications — because those belong to whoever
 * is looking at them and most of those people are not admins.
 *
 * The same triggers as the personal screen, including @mentions.
 */

const TRIGGERS: Array<{ value: string; label: string }> = [
  { value: 'task_assigned', label: 'Task assigned' },
  { value: 'at_risk', label: 'Flagged At Risk' },
  { value: 'collaborator_added', label: 'Added as collaborator' },
  { value: 'mention', label: '@mentioned on a task' },
  { value: 'comment_added', label: 'Comment on a task I am on' },
  { value: 'due_soon', label: 'Due soon' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'weekly_digest', label: 'Monday digest' },
];

const LEAD_MIN = 4;
const LEAD_MAX = 168;

interface Defaults {
  muted_types: string[];
  due_soon_lead_hours: number;
  quiet_hours: { start: string; end: string; timezone: string } | null;
  configured: boolean;
}

const NotificationDefaultsCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState<Defaults | null>(null);
  const [lead, setLead] = useState(48);
  const [muted, setMuted] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/integrations/slack/mappings/defaults');
      setDefaults(data.defaults);
      setLead(data.defaults.due_soon_lead_hours);
      setMuted(new Set(data.defaults.muted_types || []));
    } catch {
      // A tenant that cannot use integrations never renders this card at all,
      // so a failure here is a real fault rather than a permission answer.
      toast.error('Could not load the notification defaults.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/integrations/slack/mappings/defaults', {
        muted_types: [...muted],
        due_soon_lead_hours: lead,
      });
      setDefaults(data.defaults);
      toast.success('Saved. This applies to people mapped from now on.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save the defaults.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <Users className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">Defaults for new people</h2>
          <p className="mt-1 text-sm text-gray-600">
            What someone starts with the first time they are linked to Slack. Changing this{' '}
            <span className="font-medium text-gray-900">does not</span> alter anyone's existing
            settings — people manage their own under Settings → Notifications.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">Switched on by default</p>
            <div className="flex flex-wrap gap-2">
              {TRIGGERS.map((t) => {
                const on = !muted.has(t.value);
                return (
                  <button
                    key={t.value}
                    type="button"
                    disabled={saving}
                    aria-pressed={on}
                    onClick={() => {
                      const next = new Set(muted);
                      if (on) next.add(t.value);
                      else next.delete(t.value);
                      setMuted(next);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500 line-through'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">
              Due-date warning: <span className="tabular-nums">{lead} hours</span> ahead
            </p>
            <input
              type="range"
              min={LEAD_MIN}
              max={LEAD_MAX}
              step={4}
              value={lead}
              disabled={saving}
              onChange={(e) => setLead(Number(e.target.value))}
              className="w-full accent-blue-600"
              aria-label="Default hours of warning before a task is due"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
            {defaults && !defaults.configured && (
              <span className="text-xs text-gray-500">
                Nothing saved yet — showing the platform defaults.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDefaultsCard;
